import { ZoneCodeAndType } from '@components/cockpit/panels/zoneManagement/zoneManagement.types';
import {
  ZoneActiveStatusEnum,
  ZoneManagementFiltersType,
} from '@components/cockpit/panels/zoneManagement/ZoneManagementFilters.types';
import { toPlatformPerimeterFeatures } from '@components/map/layers/platforms/platform.mapper';
import { toEditablePoiFeatures } from '@components/map/layers/poi/poi.mapper';
import { toRoundSectionLineFeatures } from '@components/map/layers/round/roundSection.mapper';
import {
  toIMZFeatures,
  toIMZMarginFeatures,
  toNFZ3dFeatures,
  toNFZFeatures,
  toNIZFeatures,
} from '@components/map/layers/zones/zone.mapper';
import { hasRole, selectActiveGroup, selectActiveGroupOrganization } from '@redux/authent/authent.selectors';
import { selectMapNavigationOptions } from '@redux/maps/maps.selectors';
import { selectContext, selectCurrentBaseLayerId, selectHiddenFilterKeys } from '@redux/settings/settings.selectors';
import { selectPointOfInterestFeatures, selectSitacZonesFilteredFeatures } from '@redux/situation/situation.selectors';
import { RootState } from '@redux/store';
import { createSelector, EntityId } from '@reduxjs/toolkit';
import { selectParameter, selectParameterWithReplayMode } from '@utils/common.utils';
import { EMPTY_FEATURES, mapStyles } from '@utils/map/map.constants';
import { filterFeaturesByZoneType, isSolid3dPolygonValid } from '@utils/map/zone.utils';
import { isValidPolygon } from '@utils/validation/coordinates.utils';
import { Feature, FeatureCollection } from 'geojson';
import { StyleSpecification } from 'react-map-gl/mapbox';

import { Group } from '@/types/authent/groups.types';
import { RoleEnum } from '@/types/authent/roles.types';
import {
  InitializationMaskingZone,
  NoFlyZone,
  NoFlyZone3d,
  NoInitZone,
  Organization,
  Platform,
  SEE_POI,
  Zone,
  Zone2DTypeEnum,
  Zone3DTypeEnum,
  ZoneFormTypeEnum,
  ZoneToEditType,
  ZoneTypeEnum,
} from '@/types/config/config.types';
import { GridItemComponentEnum, GridLayout } from '@/types/config/gridLayout.types';
import { Home } from '@/types/config/home.types';
import { MergingConfiguration } from '@/types/config/merging.types';
import { ProcedureModelStateEnum } from '@/types/config/procedure.types';
import { RoundConfiguration, RoundSection } from '@/types/config/round.types';
import {
  ActiveGlobalScreenConfiguration,
  ContextEnum,
  GlobalScreenConfiguration,
} from '@/types/config/screenConfiguration.types';
import { MapLayer } from '@/types/config/settings.types';
import { NimbleConfiguration } from '@/types/config/videoServer.types';
import { filterTriggerToFilterKey, FilterTypeEnum } from '@/types/filters.types';
import { BaseStyleRaster, FeatureTypeEnum, MapIdEnum } from '@/types/map.types';

import {
  globalScreenConfigurationAdapter,
  gridConfigurationsAdapter,
  imzAdapter,
  layoutsAdapter,
  nfz3dAdapter,
  nfzAdapter,
  nizAdapter,
  nnzAdapter,
  organizationsAdapter,
  platformsAdapter,
  poiAdapter,
  procedureModelsAdapter,
  roundConfigurationEntityAdapter,
  videoStreamsAdapter,
  zoiAdapter,
} from './config.adapters';
import { ConfigState } from './config.reducer';

function selectConfiguration(state: RootState): ConfigState {
  return state.config;
}

const { selectAll: selectAllNoFlyZones } = nfzAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).noFlyZones,
);
export const NoFlyZoneSelectors = {
  selectAllNoFlyZones,
};

const { selectAll: selectAllNoFlyZones3d } = nfz3dAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).noFlyZones3d,
);
export const NoFlyZone3dSelectors = {
  selectAllNoFlyZones3d,
};

const { selectAll: selectAllNoInitZones } = nizAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).noInitZones,
);
export const NoInitZoneSelectors = {
  selectAllNoInitZones,
};

const { selectAll: selectAllInitializationMaskingZones } = imzAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).initializationMaskingZones,
);
export const InitializationMaskingZoneSelectors = {
  selectAllInitializationMaskingZones,
};

export const selectAllZonesByManagementFilters = createSelector(
  [
    selectAllNoFlyZones,
    selectAllNoFlyZones3d,
    selectAllNoInitZones,
    selectAllInitializationMaskingZones,
    selectParameter<ZoneManagementFiltersType>,
  ],
  (noFlyZones, noFlyZones3d, noInitZones, initMaskingZones, filters: ZoneManagementFiltersType) => {
    let zones: Zone[];

    switch (filters.type) {
      case ZoneTypeEnum.NIZ:
        zones = noInitZones;
        break;
      case ZoneTypeEnum.NFZ:
        zones = noFlyZones;
        break;
      case ZoneTypeEnum.NFZ_3D:
        zones = noFlyZones3d;
        break;
      case ZoneTypeEnum.IMZ:
        zones = initMaskingZones;
        break;
      case null:
      default:
        zones = [...noFlyZones, ...noFlyZones3d, ...noInitZones, ...initMaskingZones];
    }

    if (filters.activeStatus.size !== 0 && filters.activeStatus.size !== 2) {
      if (filters.activeStatus.has(ZoneActiveStatusEnum.ACTIVATED)) {
        return zones.filter((zone) => zone.active);
      } else {
        return zones.filter((zone) => !zone.active);
      }
    }

    return zones;
  },
);

export const selectZoneByCodeAndZoneType = createSelector(
  [selectAllNoFlyZones, selectAllNoInitZones, selectAllInitializationMaskingZones, selectParameter<ZoneCodeAndType>],
  (noFlyZones, noInitZones, initMaskingZones, codeAndType): ZoneToEditType | null => {
    switch (codeAndType.type) {
      case Zone2DTypeEnum.IMZ:
        return initMaskingZones.find((zone) => zone.code === codeAndType.code) ?? null;
      case Zone2DTypeEnum.NFZ_INFO:
      case Zone2DTypeEnum.NFZ_WARNING:
      case Zone2DTypeEnum.NFZ_CRITICAL:
        return noFlyZones.find((zone) => zone.code === codeAndType.code) ?? null;
      case Zone2DTypeEnum.NIZ:
        return noInitZones.find((zone) => zone.code === codeAndType.code) ?? null;
      default:
        return null;
    }
  },
);

export const selectNoFlyZones = createSelector(selectAllNoFlyZones, (nfzs): NoFlyZone[] =>
  nfzs.filter((nfz) => isValidPolygon(nfz.polygon)),
);
export const selectNoFlyZoneFeatures = createSelector(selectNoFlyZones, (nfzs): Feature[] => toNFZFeatures(nfzs));
export const selectNoFlyZonesNamesAndIds = createSelector(selectNoFlyZones, (nfzs): { id: number; name: string }[] =>
  nfzs.map((nfz) => ({ id: nfz.id, name: nfz.name })),
);

export const selectNoFlyZones3d = createSelector(selectAllNoFlyZones3d, (nfzs): NoFlyZone3d[] =>
  nfzs.filter((nfz: NoFlyZone3d) => isSolid3dPolygonValid(nfz.solidPolygon)),
);

export const selectNoFlyZone3dFeatures = createSelector(selectNoFlyZones3d, (nfzs): Feature[] => toNFZ3dFeatures(nfzs));

export const selectNoInitZones = createSelector(selectAllNoInitZones, (nizs): NoInitZone[] =>
  nizs.filter((niz: NoInitZone) => isValidPolygon(niz.polygon)),
);

export const selectNoInitZoneFeatures = createSelector(selectNoInitZones, (nizs: NoInitZone[]): Feature[] =>
  toNIZFeatures(nizs),
);

export const selectInitMaskingZones = createSelector(
  selectAllInitializationMaskingZones,
  (imzs): InitializationMaskingZone[] => imzs.filter((imz) => isValidPolygon(imz.polygon)),
);

export const selectInitMaskingZoneFeatures = createSelector(selectInitMaskingZones, (imzs): Feature[] => [
  ...toIMZFeatures(imzs),
  ...toIMZMarginFeatures(imzs),
]);

export const selectConfigZonesFeatures = createSelector(
  [selectNoFlyZoneFeatures, selectNoFlyZone3dFeatures, selectNoInitZoneFeatures, selectInitMaskingZoneFeatures],
  (nfzFeatures: Feature[], nfz3dFeatures: Feature[], nizFeatures: Feature[], imzFeatures: Feature[]): Feature[] => [
    ...nfzFeatures,
    ...nfz3dFeatures,
    ...nizFeatures,
    ...imzFeatures,
  ],
);

export const selectZonesFeatures = createSelector(
  [
    (state, mapId) => selectSitacZonesFilteredFeatures(state, mapId === MapIdEnum.REPLAY),
    (state, mapId) => selectMapNavigationOptions(state, mapId),
    (state) => selectConfigZonesFeatures(state),
  ],
  (featuresFromSituation, selectedOptions, featuresFromConfig): FeatureCollection => {
    if (selectedOptions?.at(-1)?.includes('see')) {
      let features: Feature[] = [];

      const zoneType: ZoneFormTypeEnum | undefined = [
        ...Object.values(Zone2DTypeEnum),
        ...Object.values(Zone3DTypeEnum),
      ].find((value) => value === selectedOptions.at(-1)?.split('_')[1]);

      if (zoneType) {
        features = filterFeaturesByZoneType(featuresFromConfig, zoneType);
      }

      return { type: 'FeatureCollection', features };
    }
    return { type: 'FeatureCollection', features: featuresFromSituation };
  },
);

const { selectAll: selectAllNoNeutralizationZones } = nnzAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).noNeutralizationZones,
);
export const NoNeutralizationZoneSelectors = {
  selectAllNoNeutralizationZones,
};

const { selectAll: selectAllZoneOfInterests } = zoiAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).zoneOfInterests,
);
export const ZoneOfInterestSelectors = {
  selectAllZoneOfInterests,
};

const { selectAll: selectAllPointOfInterests } = poiAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).pointOfInterests,
);
export const PointOfInterestSelectors = {
  selectAllPointOfInterests,
};

export const selectPoiBySelectedOptions = createSelector(
  [
    (state, mapId) => selectPointOfInterestFeatures(state, mapId === MapIdEnum.REPLAY),
    (state, mapId) => selectMapNavigationOptions(state, mapId),
    (state) => selectAllPointOfInterests(state),
  ],
  (mapPoiFeatures, selectedOptions, configPois): FeatureCollection => {
    if (selectedOptions?.at(-1) === SEE_POI) {
      return { type: 'FeatureCollection', features: toEditablePoiFeatures(configPois) };
    }
    return mapPoiFeatures;
  },
);

const { selectAll: selectAllRoundConfigurations } = roundConfigurationEntityAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).roundConfigurations,
);
export const RoundConfigurationSelector = {
  selectAllRoundConfigurations,
};

export const selectActiveRoundConfigurations = createSelector(
  [selectAllRoundConfigurations],
  (roundConfigurations: RoundConfiguration[]): RoundConfiguration[] =>
    roundConfigurations.filter((roundConfig) => roundConfig.active),
);

export const selectRoundSections = createSelector(
  [selectActiveRoundConfigurations],
  (roundConfigurations: RoundConfiguration[]): RoundSection[] =>
    roundConfigurations.flatMap((roundConfig) => roundConfig.sections),
);

export const selectRoundSectionsFeatures = createSelector(
  [selectRoundSections, selectParameterWithReplayMode<number | undefined>],
  (sections, zoom) => {
    const roundSectionsFeatures = toRoundSectionLineFeatures(sections, zoom);

    return {
      type: 'FeatureCollection',
      features: [...roundSectionsFeatures],
    } as FeatureCollection;
  },
);

const { selectAll: selectAllGlobalScreenConfigurations } = globalScreenConfigurationAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).globalScreenConfigurations,
);

const selectGlobalScreenConfigurationByCode = globalScreenConfigurationAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).globalScreenConfigurations,
).selectById as (state: RootState, id: string) => GlobalScreenConfiguration | undefined;

export const GlobalScreenConfigurationSelectors = {
  selectAllGlobalScreenConfigurations,
  selectGlobalScreenConfigurationByCode,
};

export const selectGlobalScreenConfigurationById = createSelector(
  [selectAllGlobalScreenConfigurations, selectParameter<number | null | undefined>],
  (globalScreenConfigurations, id): GlobalScreenConfiguration | undefined =>
    globalScreenConfigurations.find((config) => config.id === id),
);

export const selectActiveGlobalScreenConfiguration = createSelector(
  selectConfiguration,
  (state: ConfigState): ActiveGlobalScreenConfiguration | null => state.activeGlobalScreenConfiguration,
);

export const selectActiveScreenConfigurationPosition = createSelector(
  selectActiveGlobalScreenConfiguration,
  (activeScreenConfig: ActiveGlobalScreenConfiguration | null) => activeScreenConfig?.position ?? null,
);

export const selectActiveScreenConfigurationMapZoom = createSelector(
  selectActiveGlobalScreenConfiguration,
  (activeScreenConfig: ActiveGlobalScreenConfiguration | null) => activeScreenConfig?.mapZoom ?? 15,
);

const { selectAll: selectAllLayouts } = layoutsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).layouts,
);

const selectLayoutByCode = layoutsAdapter.getSelectors((state: RootState) => selectConfiguration(state).layouts)
  .selectById as (state: RootState, id: string) => GridLayout | undefined;
export const LayoutSelectors = {
  selectAllLayouts,
  selectLayoutByCode,
};

export const selectStatisticsLayoutIdInActiveConfig = (state: RootState): 1 | 2 | null => {
  const active = selectActiveGlobalScreenConfiguration(state);
  if (!active) return null;

  const screenWithStatisticsMap = active.screenConfigurations
    .filter((sc) => sc.context === ContextEnum.STAT_DATA)
    .find((sc) =>
      sc.gridLayout?.items.some(
        (item) => item.component === GridItemComponentEnum.MAP && item.selectedMap === MapIdEnum.STATISTICS,
      ),
    );

  return screenWithStatisticsMap?.code ? (Number(screenWithStatisticsMap.code) as 1 | 2) : null;
};

export const selectIsPerimeterDoubtCheckInScreenConfiguration = createSelector(
  [selectActiveGlobalScreenConfiguration, selectContext],
  (activeGlobalScreenConfiguration: ActiveGlobalScreenConfiguration | null, context: ContextEnum): boolean => {
    if (!activeGlobalScreenConfiguration) return false;

    return activeGlobalScreenConfiguration.screenConfigurations
      .filter((sc) => sc.context === context)
      .some(
        (sc) =>
          sc.gridLayout &&
          sc.gridLayout.items.some((item) => item.component === GridItemComponentEnum.PERIMETER_DOUBT_CHECK),
      );
  },
);

const { selectAll: selectAllGridConfiguration } = gridConfigurationsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).gridConfigurations,
);
export const GridConfigurationSelectors = {
  selectAllGridConfiguration,
};

const { selectAll: selectAllVideoStreams } = videoStreamsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).videoStreams,
);
export const VideoStreamSelectors = {
  selectAllVideoStreams,
};

export const selectHome = createSelector(selectConfiguration, (state: ConfigState): Home | null => state.home);

export const selectMapLayers = createSelector(
  (state) => selectActiveGroupOrganization(state),
  (organization: Organization | null): MapLayer[] => organization?.mapLayers ?? [],
);

export const selectMapLayerById = createSelector(
  [selectMapLayers, (_: RootState, id: number) => id],
  (mapLayers: MapLayer[], id) => mapLayers.find((layer) => layer.id === id),
);

export const selectMapLayersByIds = createSelector(
  [selectMapLayers, (_: RootState, ids: number[]) => ids],
  (mapLayers: MapLayer[], ids: number[]) => mapLayers.filter((layer) => ids.includes(layer.id)),
);

export const selectBaseLayer = createSelector(
  [selectCurrentBaseLayerId, selectMapLayers],
  (key: number, mapLayers: MapLayer[]): StyleSpecification | BaseStyleRaster | undefined => {
    const layer = mapLayers.find((mapLayer) => mapLayer.id === key);
    if (!layer) return undefined;

    if (layer.code in mapStyles) {
      return mapStyles[layer.code];
    }

    return layer.url ? { baseStyle: mapStyles.baseStyle, url: layer.url } : undefined;
  },
);

export const selectMergingConfiguration = createSelector(
  selectConfiguration,
  (state: ConfigState): MergingConfiguration | null => state.mergingConfiguration,
);

export const selectVideoConfiguration = createSelector(
  selectConfiguration,
  (state: ConfigState): NimbleConfiguration | null => state.videoConfiguration,
);

const { selectAll: selectAllPlatforms } = platformsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).platforms,
);

export const PlatformSelectors = {
  selectAllPlatforms,
};

export const selectPlatformByCode = platformsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).platforms,
).selectById as (state: RootState, id: EntityId) => Platform | undefined;

export const selectActiveGroupsPlatforms = createSelector(
  [selectAllPlatforms, (state) => selectActiveGroup(state), (state: RootState) => hasRole(state, RoleEnum.ADMIN)],
  (platforms: Platform[], activeGroup: Group | null, isAdmin): Platform[] => {
    if (isAdmin) {
      return platforms;
    }
    if (!activeGroup) {
      return [];
    }
    return platforms.filter((platform) => activeGroup.platforms.includes(platform.code));
  },
);

export const selectPlatformPerimeterFeatures = createSelector(
  [selectActiveGroupsPlatforms, selectHiddenFilterKeys],
  (platforms, hiddenKeys) => {
    const key = filterTriggerToFilterKey[FilterTypeEnum.ZONE][FeatureTypeEnum.PLATFORM_PERIMETER];

    const features = key && hiddenKeys.includes(key) ? EMPTY_FEATURES : toPlatformPerimeterFeatures(platforms);

    return {
      type: 'FeatureCollection',
      features,
    };
  },
);

export const makeSelectPlatformsNamesByCode = () =>
  createSelector([selectAllPlatforms, (_: RootState, contexts: string[]) => contexts], (platforms, contexts) =>
    contexts.map((context) => platforms.find((platform) => platform.code === context)?.name ?? context),
  );

const { selectAll: selectAllProcedureModels } = procedureModelsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).procedureModels,
);

export const ProcedureModelSelectors = {
  selectAllProcedureModels,
};

export const selectUsableProcedureModels = createSelector(selectAllProcedureModels, (procedureModels) =>
  procedureModels.filter((procedureModel) => procedureModel.state === ProcedureModelStateEnum.USABLE),
);

export const { selectAll: selectAllOrganizations } = organizationsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).organizations,
);

export const selectOrganizationByCode = organizationsAdapter.getSelectors(
  (state: RootState) => selectConfiguration(state).organizations,
).selectById as (state: RootState, id: EntityId) => Organization | undefined;
